"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronRight } from "@/components/icons";
import type { ResourceGroup } from "@content/resources/sections";
import { cn } from "@/lib/utils";

export interface SidebarLinkItem {
  title: string;
  href: string;
}

export interface SidebarSection {
  slug: string;
  title: string;
  href: string;
  group: ResourceGroup;
  entries: ReadonlyArray<SidebarLinkItem>;
}

const GROUPS: ReadonlyArray<ResourceGroup> = ["Start here", "Reference", "Tools"];

function isWithin(section: SidebarSection, pathname: string): boolean {
  if (pathname === section.href) return true;
  return section.entries.some(
    (entry) => pathname === entry.href || pathname.startsWith(`${entry.href}/`),
  );
}

function SidebarLink({
  href,
  label,
  pathname,
}: {
  href: string;
  label: string;
  pathname: string;
}) {
  const isCurrent = pathname === href;
  return (
    <li>
      <Link
        href={href}
        aria-current={isCurrent ? "page" : undefined}
        className={cn(
          "block px-2 py-1 text-sm transition-colors hover:bg-muted hover:text-foreground",
          isCurrent ? "bg-action-tint font-medium text-action" : "text-muted-foreground",
        )}
      >
        {label}
      </Link>
    </li>
  );
}

function SectionGroup({ section, pathname }: { section: SidebarSection; pathname: string }) {
  const isOpen = isWithin(section, pathname);
  if (section.entries.length === 0) {
    return (
      <Link
        href={section.href}
        aria-current={pathname === section.href ? "page" : undefined}
        className={cn(
          "block px-2 py-1.5 text-sm font-medium hover:bg-muted",
          pathname === section.href ? "bg-action-tint text-action" : "text-foreground",
        )}
      >
        {section.title}
      </Link>
    );
  }
  return (
    <details open={isOpen} className="group/section">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm font-medium text-foreground hover:bg-muted [&::-webkit-details-marker]:hidden">
        {section.title}
        <ChevronRight
          className="size-4 shrink-0 text-fg-subtle transition-transform group-open/section:rotate-90"
          aria-hidden
        />
      </summary>
      <ul className="mb-2 ml-2 border-l border-border pl-3">
        <SidebarLink href={section.href} label="Overview" pathname={pathname} />
        {section.entries.map((entry) => (
          <SidebarLink key={entry.href} href={entry.href} label={entry.title} pathname={pathname} />
        ))}
      </ul>
    </details>
  );
}

function SidebarNav({
  sections,
  pathname,
}: {
  sections: ReadonlyArray<SidebarSection>;
  pathname: string;
}) {
  return (
    <nav aria-label="Resources" className="flex flex-col gap-1">
      <Link
        href="/resources"
        aria-current={pathname === "/resources" ? "page" : undefined}
        className={cn(
          "px-2 py-1.5 text-sm font-medium hover:bg-muted",
          pathname === "/resources" ? "bg-action-tint text-action" : "text-foreground",
        )}
      >
        Overview
      </Link>
      {GROUPS.map((group) => (
        <div key={group} className="mt-3">
          <p className="px-2 pb-1 text-xs font-semibold text-muted-foreground">{group}</p>
          {sections
            .filter((section) => section.group === group)
            .map((section) => (
              <SectionGroup key={section.slug} section={section} pathname={pathname} />
            ))}
        </div>
      ))}
    </nav>
  );
}

export function DocsSidebar({ sections }: { sections: ReadonlyArray<SidebarSection> }) {
  const pathname = usePathname();
  return (
    <>
      <details key={pathname} className="mb-6 rounded-sm border border-border bg-card px-3 py-2 lg:hidden">
        <summary className="cursor-pointer text-sm font-semibold text-foreground">
          Browse resources
        </summary>
        <div className="mt-3">
          <SidebarNav sections={sections} pathname={pathname} />
        </div>
      </details>
      <aside className="sticky top-24 hidden max-h-[calc(100dvh-7rem)] overflow-y-auto border-r border-border pr-4 lg:block">
        <SidebarNav sections={sections} pathname={pathname} />
      </aside>
    </>
  );
}
